import { c as _c } from "react/compiler-runtime";
import type { Address } from '@morpho-org/blue-sdk';
import { isDefined } from '@morpho-org/morpho-ts';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useWeb3Context } from '@/contexts/Web3Context';
import { useChangeAfterFetching } from '@/hooks/useChangeAfterFetching';
import { useLatestBlock } from '@/hooks/useLatestBlock';
import { useFeatureFlags } from '@/lib/shared/components/FeatureFlagsProvider';
import { computeMaxForceDeallocate, EMPTY_RESULT } from '../VaultV2SimpleActionPanel/VaultV2OperationPanel/VaultV2ReviewDialog/deallocation';
import { useVaultV2FactoryCheck } from '../VaultV2SimpleActionPanel/VaultV2OperationPanel/VaultV2ReviewDialog/useVaultV2FactoryCheck';

/**
 * Fetches and polls vault data every 10 seconds, pinned to the latest block so
 * the snapshot and every adapter read resolve at the same block.
 * @param vaultAddress - Vault contract address
 * @param options.enabled - When false, skips every on-chain read (block, factory
 *   check, vault data). Defaults to true.
 * @returns Object with accrual vault data and loading state
 */
export function useVaultV2(vaultAddress, options) {
  const $ = _c(42);
  const isEnabled = options?.enabled ?? true;
  const {
    viemClient,
    targetChainId,
    account
  } = useWeb3Context();
  const {
    forceWithdraw: isForceWithdrawEnabled
  } = useFeatureFlags();
  let t0;
  if ($[0] !== isEnabled || $[1] !== vaultAddress) {
    t0 = {
      vaultAddress,
      enabled: isEnabled
    };
    $[0] = isEnabled;
    $[1] = vaultAddress;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const {
    isCorrectVaultV2,
    isLoading: isLoadingFactoryCheck,
    error: correctVaultV2Error
  } = useVaultV2FactoryCheck(t0);
  let t1;
  if ($[3] !== targetChainId || $[4] !== vaultAddress || $[5] !== viemClient.morpho) {
    t1 = viemClient.morpho.vaultV2(vaultAddress, targetChainId);
    $[3] = targetChainId;
    $[4] = vaultAddress;
    $[5] = viemClient.morpho;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const vault = t1;
  const t2 = account.isConnected && isEnabled;
  let t3;
  if ($[7] !== t2) {
    t3 = {
      enabled: t2
    };
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const {
    data: block,
    isLoading: isBlockLoading,
    isFetching: isFetchingBlock,
    error: blockError,
    refetch: refetchVaultData
  } = useLatestBlock(targetChainId, t3);
  let blockNumber;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[9] !== account.isConnected || $[10] !== block?.number || $[11] !== isEnabled || $[12] !== targetChainId || $[13] !== vault || $[14] !== vaultAddress) {
    blockNumber = block?.number?.toString();
    t7 = useQuery;
    t4 = ["vaultData", vaultAddress, targetChainId, blockNumber];
    t5 = () => vault.getData({
      blockNumber: BigInt(blockNumber)
    });
    t6 = account.isConnected && isDefined(blockNumber) && isEnabled;
    $[9] = account.isConnected;
    $[10] = block?.number;
    $[11] = isEnabled;
    $[12] = targetChainId;
    $[13] = vault;
    $[14] = vaultAddress;
    $[15] = blockNumber;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
  } else {
    blockNumber = $[15];
    t4 = $[16];
    t5 = $[17];
    t6 = $[18];
    t7 = $[19];
  }
  let t8;
  if ($[20] !== t4 || $[21] !== t5 || $[22] !== t6) {
    t8 = {
      queryKey: t4,
      queryFn: t5,
      enabled: t6,
      placeholderData: keepPreviousData,
      staleTime: Infinity,
      gcTime: 20000
    };
    $[20] = t4;
    $[21] = t5;
    $[22] = t6;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  const {
    data: _vaultData,
    isLoading: isVaultDataLoading,
    isFetching: isFetchingVaultData,
    isPlaceholderData: isVaultDataPlaceholder,
    error: vaultDataError
  } = t7(t8);
  let t9;
  bb0: {
    if (!isCorrectVaultV2 || !isDefined(_vaultData) || !isDefined(block)) {
      t9 = undefined;
      break bb0;
    }
    if (_vaultData.lastUpdate > block.timestamp) {
      t9 = _vaultData;
      break bb0;
    }
    let t10;
    if ($[24] !== _vaultData || $[25] !== block.timestamp) {
      t10 = _vaultData.accrueInterest(block.timestamp);
      $[24] = _vaultData;
      $[25] = block.timestamp;
      $[26] = t10;
    } else {
      t10 = $[26];
    }
    t9 = t10.vault;
  }
  const validatedVaultData = t9;
  const memoizedBlockError = useChangeAfterFetching(blockError, isFetchingBlock);
  const memoizedVaultDataError = useChangeAfterFetching(vaultDataError, isFetchingVaultData);
  let t10;
  bb1: {
    if (!isDefined(validatedVaultData) || !isForceWithdrawEnabled) {
      t10 = EMPTY_RESULT;
      break bb1;
    }
    let t11;
    if ($[27] !== validatedVaultData) {
      t11 = computeMaxForceDeallocate(validatedVaultData);
      $[27] = validatedVaultData;
      $[28] = t11;
    } else {
      t11 = $[28];
    }
    t10 = t11;
  }
  const {
    totalDeallocatable,
    totalCoverage,
    actions: forceDeallocateActions
  } = t10;
  const t11 = block?.timestamp;
  const t12 = !!isCorrectVaultV2;
  const t13 = isVaultDataLoading || isLoadingFactoryCheck || isBlockLoading;
  const t14 = correctVaultV2Error || memoizedBlockError || memoizedVaultDataError;
  let t15;
  if ($[29] !== blockNumber || $[30] !== forceDeallocateActions || $[31] !== isVaultDataPlaceholder || $[32] !== refetchVaultData || $[33] !== t11 || $[34] !== t12 || $[35] !== t13 || $[36] !== t14 || $[37] !== totalCoverage || $[38] !== totalDeallocatable || $[39] !== validatedVaultData || $[40] !== vault) {
    t15 = {
      vaultData: validatedVaultData,
      refetchVaultData,
      vaultDataBlockNumber: blockNumber,
      vaultDataTimestamp: t11,
      isVaultDataPlaceholder,
      vault,
      isCorrectVaultV2: t12,
      isLoading: t13,
      error: t14,
      totalDeallocatable,
      totalCoverage,
      forceDeallocateActions
    };
    $[29] = blockNumber;
    $[30] = forceDeallocateActions;
    $[31] = isVaultDataPlaceholder;
    $[32] = refetchVaultData;
    $[33] = t11;
    $[34] = t12;
    $[35] = t13;
    $[36] = t14;
    $[37] = totalCoverage;
    $[38] = totalDeallocatable;
    $[39] = validatedVaultData;
    $[40] = vault;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  return t15;
}